/**
 * The bind-frame ANCHOR references the intake check measures against — the
 * per-bone frame facts (`bindFrameReference`) of each anchor rest skeleton,
 * copied verbatim from `mapping/bind-frame-check.expected.json` (the file World
 * pins its numbers from; #194). `bind-frame-anchors.test.ts` pins this file to
 * that one, so the two can never drift. Keyed by BBSA ontology id.
 *
 *   tripo-2026-handed — the family clip rest (library-salute.glb, 0.7.0)
 *   vrm               — World's VRoid clip donor rest (M_Walk_001.glb)
 *
 * Generated; do not edit by hand. Regenerate by re-running the expected-file
 * writer, then copying `anchors.<id>.reference` here.
 *
 * Depends on: mapping/bind-frame-check (types). Depended on by: intake/rig-check.
 */
import type { AnatomicalFrame, BoneFrameFacts } from '../mapping/bind-frame-check.js';

export interface BindFrameAnchor {
  readonly source: string;
  readonly frame: AnatomicalFrame;
  readonly reference: Readonly<Record<string, BoneFrameFacts>>;
}

export const BIND_FRAME_ANCHORS: Readonly<Record<'tripo-2026-handed' | 'vrm', BindFrameAnchor>> = {
  "tripo-2026-handed": {
    "source": "packages/adapter-three/fixtures/specimens/library-salute.glb (tripo-2026-handed 0.7.0 clip rest)",
    "frame": {
      "forward": {
        "x": 0,
        "y": 0,
        "z": 1
      },
      "up": {
        "x": 0,
        "y": 1,
        "z": 0
      }
    },
    "reference": {
      "Spine": {
        "localAxis": {
          "x": 7.177357146115338e-9,
          "y": 0.9999999999999798,
          "z": -2.0160160864313162e-7
        },
        "localForward": {
          "x": -0.0011850761847979243,
          "y": -2.0159296136579297e-7,
          "z": -0.9999992977969514
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 0.018477609890320094,
          "y": 0.9977823723474201,
          "z": -0.0639446273387812
        }
      },
      "Spine1": {
        "localAxis": {
          "x": 2.9082176524614093e-8,
          "y": 0.9999999999999987,
          "z": 4.286070406922527e-8
        },
        "localForward": {
          "x": -0.001184119123514829,
          "y": 4.289511075505902e-8,
          "z": -0.999999298930704
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 0.0184775044940141,
          "y": 0.9977823373389482,
          "z": -0.06394520405862264
        }
      },
      "Spine2": {
        "localAxis": {
          "x": -1.8497973812425794e-8,
          "y": 0.9999999999999999,
          "z": 5.102712044569024e-9
        },
        "localForward": {
          "x": -0.0011846101525568747,
          "y": 5.080795589806897e-9,
          "z": -0.999999298349147
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 0.018477565113246535,
          "y": 0.9977823391377114,
          "z": -0.06394515847476645
        }
      },
      "Neck": {
        "localAxis": {
          "x": -2.8916546032653482e-8,
          "y": 0.9999999999999944,
          "z": -1.009780845606656e-7
        },
        "localForward": {
          "x": 0.0056250667947319605,
          "y": -1.0081382928461832e-7,
          "z": -0.9999841791866231
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 0.018477495819161346,
          "y": 0.9564852964463593,
          "z": 0.2911948829051994
        }
      },
      "LeftArm": {
        "localAxis": {
          "x": -0.00005766262673267962,
          "y": 0.9999999348981434,
          "z": 0.0003562004078661029
        },
        "localForward": {
          "x": 0.05686021744083264,
          "y": -0.00035234542001869734,
          "z": 0.9983820869413105
        },
        "localUp": {
          "x": -0.9999999962213019,
          "y": -0.00005768579629605862,
          "z": 0.00006503649448132141
        },
        "anatomicalAxis": {
          "x": 0.9382227290835655,
          "y": -0.1809589641560845,
          "z": -0.2949440013333773
        }
      },
      "LeftForeArm": {
        "localAxis": {
          "x": -0.07300629442683726,
          "y": 0.9971414065717028,
          "z": -0.019470394815408437
        },
        "localForward": {
          "x": -0.004520059389690911,
          "y": 0.01919147779599913,
          "z": 0.9998056092276736
        },
        "localUp": {
          "x": -0.9973292716114551,
          "y": -0.07303330255030482,
          "z": -0.0006787529448560809
        },
        "anatomicalAxis": {
          "x": 0.9969744601133728,
          "y": -0.03483173996459207,
          "z": 0.06948867370074235
        }
      },
      "RightArm": {
        "localAxis": {
          "x": 0.00006214672334581282,
          "y": 0.9999999980423062,
          "z": 0.000007291946731796671
        },
        "localForward": {
          "x": -0.015614344605375446,
          "y": -0.000006320677418793566,
          "z": 0.9998780886701106
        },
        "localUp": {
          "x": 0.9999999980673153,
          "y": -0.00006214673629456602,
          "z": 0.0000017755772157949934
        },
        "anatomicalAxis": {
          "x": -0.9804645227258753,
          "y": -0.17655008088843688,
          "z": -0.08671440834260578
        }
      },
      "RightForeArm": {
        "localAxis": {
          "x": 0.08046809588036244,
          "y": 0.9896694734126902,
          "z": -0.11865588455882577
        },
        "localForward": {
          "x": 0.007430938377039174,
          "y": 0.11844297426125994,
          "z": 0.9929330506146842
        },
        "localUp": {
          "x": 0.9966750537369232,
          "y": -0.08141746939041773,
          "z": -0.0031674811067125947
        },
        "anatomicalAxis": {
          "x": -0.9799169818022867,
          "y": -0.026689611126092107,
          "z": -0.19761167332279458
        }
      },
      "LeftUpLeg": {
        "localAxis": {
          "x": 0.000027841352480101327,
          "y": 0.9999999984808554,
          "z": 0.000047572556620079074
        },
        "localForward": {
          "x": -0.0044203024924449235,
          "y": -0.00004744902467464228,
          "z": 0.9999902292894991
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 0.035069975327103785,
          "y": -0.991524751425868,
          "z": -0.1250950204461683
        }
      },
      "LeftLeg": {
        "localAxis": {
          "x": -0.000010369289228270325,
          "y": 0.9999999940110489,
          "z": 0.00010895127251395181
        },
        "localForward": {
          "x": -0.00028345903975258627,
          "y": -0.00010895420741149908,
          "z": 0.9999999538899756
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 0.003993240852077352,
          "y": -0.9973865719032591,
          "z": -0.07213931115946577
        }
      },
      "RightUpLeg": {
        "localAxis": {
          "x": 0.00008111877754187158,
          "y": 0.9999999966999772,
          "z": -0.000004448591129607715
        },
        "localForward": {
          "x": 0.000008302868906310734,
          "y": 0.000004447917625535759,
          "z": 0.9999999999556393
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": -0.00008129496568086707,
          "y": -0.9960659928340857,
          "z": -0.08861450959394788
        }
      },
      "RightLeg": {
        "localAxis": {
          "x": -0.000005622071968719446,
          "y": 0.9999999950633862,
          "z": 0.00009920493780911369
        },
        "localForward": {
          "x": 0.007900096864366431,
          "y": -0.00009915742708350995,
          "z": 0.9999687888316007
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": -0.08443912239248394,
          "y": -0.9921063126974347,
          "z": -0.09270975631230506
        }
      }
    }
  },
  "vrm": {
    "source": "World public/animations/vrm/locomotion/M_Walk_001.glb (the VRoid clip set donor rest)",
    "frame": {
      "forward": {
        "x": 0,
        "y": 0,
        "z": 1
      },
      "up": {
        "x": 0,
        "y": 1,
        "z": 0
      }
    },
    "reference": {
      "Spine": {
        "localAxis": {
          "x": 1.9182263600036095e-16,
          "y": 0.9922606202146484,
          "z": -0.1241727086409948
        },
        "localForward": {
          "x": -5.176674375789126e-18,
          "y": 0.12417270864099479,
          "z": 0.9922606202146484
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 1.9182263600036095e-16,
          "y": 0.9996360557589428,
          "z": 0.026976953621638745
        }
      },
      "Spine1": {
        "localAxis": {
          "x": -2.881364469097714e-16,
          "y": 0.9999099889141285,
          "z": -0.01341693220328487
        },
        "localForward": {
          "x": -3.920533539232048e-17,
          "y": 0.013416932203284854,
          "z": 0.9999099889141285
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": -2.8813644690977134e-16,
          "y": 0.9908697172946689,
          "z": -0.13482285914629938
        }
      },
      "Spine2": {
        "localAxis": {
          "x": -7.132233710035031e-16,
          "y": 0.9984333472054653,
          "z": 0.05595400958011033
        },
        "localForward": {
          "x": -2.067958949206967e-16,
          "y": -0.05595400958011028,
          "z": 0.9984333472054653
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": -7.13223371003503e-16,
          "y": 0.9604431441032005,
          "z": -0.2784761514836752
        }
      },
      "Neck": {
        "localAxis": {
          "x": -1.9067014080611047e-9,
          "y": 0.9999906946300804,
          "z": -0.0043140066352659314
        },
        "localForward": {
          "x": 2.2066765519972182e-10,
          "y": 0.0043140066352659176,
          "z": 0.9999906946300804
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": -1.906701408061105e-9,
          "y": 0.9933695069454559,
          "z": 0.11496531072868048
        }
      },
      "LeftArm": {
        "localAxis": {
          "x": 0.9999999999994589,
          "y": 0.0000010401252718663672,
          "z": -2.25345668527849e-8
        },
        "localForward": {
          "x": 2.2534550858183618e-8,
          "y": 1.5377583741665913e-8,
          "z": 0.9999999999999998
        },
        "localUp": {
          "x": -0.0000010401252722128935,
          "y": 0.999999999999459,
          "z": -1.5377541852256242e-8
        },
        "anatomicalAxis": {
          "x": 0.9999999999994726,
          "y": 0.0000010267923747163264,
          "z": -1.7969207761817615e-8
        }
      },
      "LeftForeArm": {
        "localAxis": {
          "x": 0.9999999981677781,
          "y": 0.000010772031663811273,
          "z": 0.000059568508238141984
        },
        "localForward": {
          "x": -0.00005956850840143238,
          "y": 1.4837899924502408e-8,
          "z": 0.9999999982257963
        },
        "localUp": {
          "x": -0.000010772030722623013,
          "y": 0.9999999999419815,
          "z": -1.6120934779935144e-8
        },
        "anatomicalAxis": {
          "x": 0.9999999981676333,
          "y": 0.000010766148427518375,
          "z": 0.00005957200276713594
        }
      },
      "RightArm": {
        "localAxis": {
          "x": -0.9999999999994589,
          "y": 0.0000010401252718663672,
          "z": -2.25345668527849e-8
        },
        "localForward": {
          "x": -2.2534550858183618e-8,
          "y": 1.5377583741665913e-8,
          "z": 0.9999999999999998
        },
        "localUp": {
          "x": 0.0000010401252722128935,
          "y": 0.999999999999459,
          "z": -1.5377541852256242e-8
        },
        "anatomicalAxis": {
          "x": -0.9999999999994726,
          "y": 0.0000010267923747163264,
          "z": -1.7969207761817615e-8
        }
      },
      "RightForeArm": {
        "localAxis": {
          "x": -0.9999999981677781,
          "y": 0.000010772031663811273,
          "z": 0.000059568508238141984
        },
        "localForward": {
          "x": 0.00005956850840143238,
          "y": 1.4837899924502408e-8,
          "z": 0.9999999982257963
        },
        "localUp": {
          "x": 0.000010772030722623013,
          "y": 0.9999999999419815,
          "z": -1.6120934779935144e-8
        },
        "anatomicalAxis": {
          "x": -0.9999999981676333,
          "y": 0.000010766148427518375,
          "z": 0.00005957200276713594
        }
      },
      "LeftUpLeg": {
        "localAxis": {
          "x": -3.7989852408324134e-8,
          "y": -0.9999999999985942,
          "z": -0.0000016763021377444454
        },
        "localForward": {
          "x": -7.950130283656665e-10,
          "y": -0.00000167630213771669,
          "z": 0.9999999999985951
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": -3.7989852408324134e-8,
          "y": -0.9997811025682772,
          "z": -0.020922403001529182
        }
      },
      "LeftLeg": {
        "localAxis": {
          "x": 0,
          "y": -0.9999785499816144,
          "z": 0.006549776841081781
        },
        "localForward": {
          "x": 0,
          "y": 0.006549776841081781,
          "z": 0.9999785499816144
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 0,
          "y": -0.9982005763565008,
          "z": -0.0599634001833578
        }
      },
      "RightUpLeg": {
        "localAxis": {
          "x": 3.7989852408324134e-8,
          "y": -0.9999999999985942,
          "z": -0.0000016763021377444454
        },
        "localForward": {
          "x": 7.950130283656665e-10,
          "y": -0.00000167630213771669,
          "z": 0.9999999999985951
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 3.7989852408324134e-8,
          "y": -0.9997811025682772,
          "z": -0.020922403001529182
        }
      },
      "RightLeg": {
        "localAxis": {
          "x": 0,
          "y": -0.9999785499816144,
          "z": 0.006549776841081781
        },
        "localForward": {
          "x": 0,
          "y": 0.006549776841081781,
          "z": 0.9999785499816144
        },
        "localUp": null,
        "anatomicalAxis": {
          "x": 0,
          "y": -0.9982005763565008,
          "z": -0.0599634001833578
        }
      }
    }
  }
};
